import { Box, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { ConnectionState } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface LiveIndicatorProps {
  state: ConnectionState;
}

/** Token rotation passes through `connecting` for a few hundred ms; don't flicker the dot for that. */
const PENDING_DEBOUNCE_MS = 2000;

type Mode = 'live' | 'pending' | 'hidden';

const useStyles = makeStyles((theme) => {
  const glowColor = alpha(theme.palette.success.main, 0.55);
  const glowColorSoft = alpha(theme.palette.success.main, 0.22);

  return {
    '@keyframes pulseGlow': {
      '0%': {
        boxShadow: `0 0 2px 0 ${glowColorSoft}`,
      },
      '30%': {
        boxShadow: `0 0 4px 2px ${glowColor}, 0 0 8px 4px ${glowColorSoft}`,
      },
      '60%': {
        boxShadow: `0 0 2px 0 ${glowColorSoft}`,
      },
      '100%': {
        boxShadow: `0 0 2px 0 ${glowColorSoft}`,
      },
    },
    live: {
      animation: '$pulseGlow 3.6s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        boxShadow: `0 0 4px 1px ${glowColorSoft}`,
      },
    },
  };
});

/**
 * A small dot next to the refresh button: green while the real-time stream is open, grey while it has
 * been reconnecting for more than two seconds, hidden when there is no live connection at all.
 */
export const LiveIndicator = ({ state }: LiveIndicatorProps) => {
  const { palette } = useCustomTheme();
  const [mode, setMode] = useState<Mode>('hidden');
  const classes = useStyles();

  useEffect(() => {
    if (state === 'open') {
      setMode('live');
      return;
    }
    if (state === 'connecting' || state === 'reconnecting' || state === 'authorizing') {
      const timer = setTimeout(() => setMode('pending'), PENDING_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }
    setMode('hidden');
  }, [state]);

  if (mode === 'hidden') return null;

  const isLive = mode === 'live';

  return (
    <StyledTooltip title={<Text tId={isLive ? 'LIVE' : 'LIVE_RECONNECTING'} />}>
      <Box
        component="span"
        display="inline-block"
        width={8}
        height={8}
        borderRadius="50%"
        marginRight={1}
        bgcolor={isLive ? palette.success.main : palette.text.disabled}
        className={isLive ? classes.live : undefined}
        aria-label={isLive ? 'live' : 'reconnecting'}
      />
    </StyledTooltip>
  );
};
